import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { strapiGet, strapiPost, strapiPatch } from '@/lib/strapi'
import { toast } from 'sonner'
import { useAppStore } from '@/lib/store/useAppStore'

export interface LinkedBooking {
  documentId: string
  guestName: string
  guestPhone?: string | null
  date: string
  time: string
  people: number
}

export interface LinkedOrder {
  documentId: string
  guestName: string
  guestPhone?: string | null
  date: string
  time: string
  fulfillmentMethod?: string
}

export interface SheAnswersConversation {
  conversation_id: string
  agent_id: string
  start_time_unix_secs: number
  call_duration_secs: number
  status: string
  call_successful: string
  phoneNumber?: string | null
  linkedBooking?: LinkedBooking
  linkedOrder?: LinkedOrder
}

export interface SheAnswersTranscriptMessage {
  role: 'agent' | 'user'
  message: string
  time_in_call_secs: number
}

export interface SheAnswersConversationDetail extends SheAnswersConversation {
  transcript: SheAnswersTranscriptMessage[]
  metadata: Record<string, unknown>
}

interface ConversationsResult {
  conversations: SheAnswersConversation[]
  next_cursor?: string
}

interface ProvisionResult {
  agentId: string
  phoneNumber: string | null
  message: string
}

export function useSheAnswersAvailableNumber(enabled = true) {
  return useQuery({
    queryKey: ['she-answers-available-number'],
    queryFn: async () => {
      const res = await strapiGet<{ phoneNumber: string | null }>('/api/she-answers/available-number')
      return res.data
    },
    enabled,
    staleTime: 30_000,
  })
}

export function useSheAnswersConversations(enabled = true, orderDate?: string) {
  return useQuery({
    queryKey: ['she-answers-conversations', orderDate ?? 'recent'],
    queryFn: async () => {
      const res = await strapiGet<ConversationsResult>('/api/she-answers/conversations', {
        pageSize: orderDate ? 100 : 30,
        ...(orderDate ? { orderDate } : {}),
      })
      return res.data
    },
    enabled,
    staleTime: 60_000,
  })
}

export function useSheAnswersConversation(conversationId: string | null) {
  return useQuery({
    queryKey: ['she-answers-conversation', conversationId],
    queryFn: async () => {
      const res = await strapiGet<SheAnswersConversationDetail>(
        `/api/she-answers/conversations/${conversationId}`,
      )
      return res.data
    },
    enabled: !!conversationId,
    staleTime: 5 * 60_000,
  })
}

export function useProvisionSheAnswers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (voiceId?: string) => {
      const res = await strapiPost<ProvisionResult>('/api/she-answers/provision', voiceId ? { voiceId } : {})
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-restaurant'] })
      qc.invalidateQueries({ queryKey: ['restaurants'] })
      const s = useAppStore.getState()
      s.setFeatures({ sheAnswers: true, customerPage: s.featureCustomerPage, pos: s.featurePOS })
      toast.success('SheAnswers activated successfully!')
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to activate SheAnswers')
    },
  })
}

export function useDeprovisionSheAnswers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await strapiPost<{ message: string }>('/api/she-answers/deprovision', {})
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-restaurant'] })
      qc.invalidateQueries({ queryKey: ['restaurants'] })
      qc.removeQueries({ queryKey: ['she-answers-conversations'] })
      const s = useAppStore.getState()
      s.setFeatures({ sheAnswers: false, customerPage: s.featureCustomerPage, pos: s.featurePOS })
      toast.success('SheAnswers deactivated')
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to deactivate SheAnswers')
    },
  })
}

export interface AgentConfig {
  firstMessage: string
  voiceId: string
  mood: string
  allowBookings: boolean
  allowOrders: boolean
  allowPromptOverwrite: boolean
  expectedToolNames?: string[]
  liveToolNames?: string[]
}

export function useAgentConfig(enabled = true) {
  return useQuery({
    queryKey: ['she-answers-agent-config'],
    queryFn: async () => {
      const res = await strapiGet<AgentConfig>('/api/she-answers/agent-config')
      return res.data
    },
    enabled,
    staleTime: 60_000,
  })
}

export function useUpdateAgentConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (config: Partial<AgentConfig>) => {
      const res = await strapiPatch<{ ok: boolean }>('/api/she-answers/agent-config', config)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['she-answers-agent-config'] })
      qc.invalidateQueries({ queryKey: ['my-restaurant'] })
      qc.invalidateQueries({ queryKey: ['restaurants'] })
      toast.success('Agent settings saved')
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Failed to save settings')
    },
  })
}

export function useSyncSheAnswers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await strapiPost<{ message: string }>('/api/she-answers/sync', {})
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['she-answers-agent-config'] })
      toast.success('Knowledge base synced')
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Sync failed')
    },
  })
}
